import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/auth.jsx'
import { useBranding } from '../context/branding.jsx'

function EyeIcon({ open }) {
  return open ? (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"/><circle cx="12" cy="12" r="3"/></svg>
  ) : (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3l18 18"/><path d="M10.6 10.6A3 3 0 0 0 12 15a3 3 0 0 0 2.4-4.8"/><path d="M9.9 5.2A10.7 10.7 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-2.1 3.1"/><path d="M6.6 6.6C3.6 8.5 2 12 2 12s3.5 7 10 7c1.4 0 2.7-.3 3.8-.8"/></svg>
  )
}

export default function Login() {
  const { login } = useAuth()
  const { site_name: siteName, logo_url: logoUrl, logo_mode: logoMode } = useBranding()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  async function onSubmit(e) {
    e.preventDefault()
    setError('')
    setBusy(true)
    try {
      const user = await login(email.trim(), password)
      if (user?.role === 'superadmin') navigate('/superadmin')
      else throw new Error('This Panelz installation is for administrators only')
    } catch (err) {
      setError(err.message || 'Sign in failed')
    } finally {
      setBusy(false)
    }
  }

  const inputStyle = {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid var(--border)',
    background: 'var(--input-bg)',
    color: 'var(--text)',
    borderRadius: 12,
    padding: '13px 14px',
    fontSize: 14,
    outline: 'none',
  }
  const labelStyle = { display: 'block', marginBottom: 8, fontSize: 11, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--muted)' }

  return (
    <div className="ct-login-page" style={{ minHeight: '100vh', background: 'linear-gradient(135deg, var(--canvas) 0%, var(--surface-muted) 100%)', display: 'grid', placeItems: 'center', padding: 24 }}>
      <div className="ct-login-shell" style={{ width: 'min(980px, 100%)', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(340px, 420px)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 24, overflow: 'hidden', boxShadow: '0 24px 70px rgba(15,23,42,.14)' }}>
        <section className="ct-login-brand" style={{ padding: '48px 46px', background: 'linear-gradient(160deg, rgba(16,185,129,.14), rgba(59,130,246,.08))', borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 42 }}>
          <div className="ct-login-brand-copy">
            <div className="ct-login-brand-header">
              <div className="ct-login-logo-lockup" style={{ width: 'min(390px, 100%)', lineHeight: 0 }}>
                {logoMode === 'custom' ? (
                  <span
                    className="ct-custom-theme-logo ct-custom-login-logo"
                    style={{ '--ct-custom-logo': `url("${logoUrl}")` }}
                    role="img"
                    aria-label={siteName}
                  />
                ) : (
                  <div className="ct-login-auto-logo" aria-label={siteName}>
                    <div className="ct-logo-mark"><span className="ct-affisee-icon" aria-hidden="true"><span className="ct-affisee-icon-layer ct-affisee-icon-white" /><span className="ct-affisee-icon-layer ct-affisee-icon-accent" /></span></div>
                    <div className="ct-logo-text"><div className="ct-logo-title">{siteName}</div><div className="ct-logo-sub">Performance Suite</div></div>
                  </div>
                )}
              </div>
            </div>
            <p className="ct-login-brand-subtitle" style={{ margin: '12px 0 0', maxWidth: 430, fontSize: 15, lineHeight: 1.7, color: 'var(--muted)' }}>
              Secure central reporting across every connected domain — private, fast and consistent.
            </p>
          </div>

          <div className="ct-login-highlight">
            <span>Panelz central reporting</span>
            <h2>All your domains. One clear view.</h2>
            <p>
              Compare clicks, conversions, revenue and traffic quality without signing in to each domain separately.
            </p>
          </div>

          <div className="ct-login-features" style={{ display: 'grid', gap: 12 }}>
            {['Encrypted database credentials', 'All Domains combined reporting', 'Source health and activity logs'].map((t) => (
              <div className="ct-login-feature" key={t} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5, fontWeight: 650, color: 'var(--text)' }}>
                <span style={{ width: 22, height: 22, borderRadius: 7, background: 'var(--accent-soft)', color: 'var(--accent)', display: 'grid', placeItems: 'center', flex: '0 0 auto' }}>✓</span>
                {t}
              </div>
            ))}
          </div>
        </section>

        <section className="ct-login-form-panel" style={{ padding: '48px 42px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div className="ct-login-form-head" style={{ marginBottom: 28 }}>
            <h2 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: 'var(--text)' }}>Welcome back</h2>
            <p style={{ margin: '8px 0 0', fontSize: 14, color: 'var(--muted)' }}>Sign in to continue to your dashboard.</p>
          </div>

          <form onSubmit={onSubmit} style={{ display: 'grid', gap: 18 }}>
            <div>
              <label style={labelStyle}>Email</label>
              <input style={inputStyle} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required />
            </div>

            <div>
              <label style={labelStyle}>Password</label>
              <div style={{ position: 'relative' }}>
                <input style={{ ...inputStyle, paddingRight: 48 }} type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required />
                <button type="button" onClick={() => setShowPassword((v) => !v)} aria-label={showPassword ? 'Hide password' : 'Show password'} title={showPassword ? 'Hide password' : 'Show password'} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', width: 30, height: 30, border: 0, background: 'transparent', color: 'var(--muted)', cursor: 'pointer', display: 'grid', placeItems: 'center' }}>
                  <EyeIcon open={showPassword} />
                </button>
              </div>
            </div>

            {error && <div style={{ padding: '10px 12px', borderRadius: 10, background: 'var(--risk-soft)', color: 'var(--risk)', fontSize: 13, border: '1px solid rgba(239,68,68,.25)' }}>{error}</div>}

            <button type="submit" disabled={busy} style={{ width: '100%', border: 0, borderRadius: 12, padding: '13px 18px', background: 'var(--accent)', color: 'var(--accent-contrast)', fontSize: 15, fontWeight: 800, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? .7 : 1, boxShadow: '0 14px 30px rgba(16,185,129,.22)' }}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </section>
      </div>
    </div>
  )
}
